import EntityType from "../../enum/EntityType";
import {
  ChatManageRelationshipAction,
  ChatRelationshipEntityType,
} from "../../types/chat/ChatContent";

// The runtime form of ChatRelationshipEntityType, for testing membership of a wire value.
export const RELATIONSHIP_ENTITY_TYPES: ChatRelationshipEntityType[] = [
  EntityType.COMPANY,
  EntityType.DEAL,
  EntityType.PERSON,
];

// Which payloads name a record on the wire. Deliberately separate from canChooseRelatedRecord, which
// answers whether the REP may change that record — a UI question that has already moved once, and
// took this guard with it when it did.
export const namesRelatedRecord = (
  action: Pick<ChatManageRelationshipAction, "operation" | "relationshipKey">
): boolean =>
  action.operation === "link" ||
  action.relationshipKey === "account.contacts" ||
  action.relationshipKey === "account.deals";

// No unlink lets the rep choose the record, whatever the key. Two different reasons land on the same
// answer:
//
// A Company-owned scalar names no record at all — the platform clears the one link the Company holds,
// so there is nothing to swap to.
//
// A collection unlink does name a record, and the platform would accept a different id as "remove
// this one instead". Web does not offer it, because a valid pick must already be linked to the anchor
// and the pickers cannot be narrowed to that set here. `DealAssociation` has no filter prop at all,
// and its `fixedCompany` only narrows the already-assigned list in multiselect mode.
// `PeopleAssociation` DOES take `requestFilters`, so the Person picker could be scoped — but only
// with the anchor's contact ids, which the proposal payload does not carry, so it needs a fetch this
// row has no reason to make. Unscoped, the picker offered records the platform then refused, under a
// line still reading "Removing from <anchor>" — a dead end the rep could not foresee. Mobile is
// read-only on every relationship payload for the same reason.
//
// This deviates from the spec's control table, which grants a swap on both collection unlinks. The
// deviation is deliberate. It is fully justified for account.deals and justified only by cost for
// account.contacts. Fetch the anchor's contacts and the Person half can come back.
//
// One predicate, three surfaces: the picker, the pencil via canEdit, and the serializer's outbound
// wire guard. The INBOUND parser asks namesRelatedRecord instead — a different question.
export const canChooseRelatedRecord = (
  action: Pick<ChatManageRelationshipAction, "operation" | "relationshipKey">
): boolean => action.operation !== "unlink";

// The platform honours the primary flag on an account.contacts link and nowhere else. Kept here
// rather than at each call site so the checkbox, the diff and the serializer cannot disagree about
// which payloads offer it.
export const supportsPrimaryFlag = (
  action: Pick<ChatManageRelationshipAction, "operation" | "relationshipKey">
): boolean => action.relationshipKey === "account.contacts" && action.operation === "link";
